import React, { useState } from "react"
import clsx from "clsx"
import Image, { ImageProps } from "next/image"

export const AppImage: React.FC<ImageProps> = ({ className, onLoad, fill, width, height, ...props }) => {
  const [loaded, setLoaded] = useState(false)

  // Above-the-fold images must paint as soon as their bytes arrive: hiding them until hydration
  // and onLoad delays LCP by the whole JS boot time.
  if (props.priority) {
    return fill ? (
      <Image fill fetchPriority="high" className={className} onLoad={onLoad} {...props} />
    ) : (
      <span className={clsx("relative overflow-hidden inline-block", className)}>
        <Image width={width} height={height} fetchPriority="high" className={className} onLoad={onLoad} {...props} />
      </span>
    )
  }

  const skeletonEl = (
    <span
      aria-hidden
      className={clsx(
        "absolute inset-0 z-10 skeleton transition-opacity duration-300",
        loaded ? "opacity-0 pointer-events-none" : "opacity-100"
      )}
    />
  )

  if (fill) {
    return (
      <>
        {skeletonEl}
        <Image
          fill
          className={clsx(
            className,
            "transition-opacity duration-300",
            loaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={(e) => {
            setLoaded(true)
            onLoad?.(e)
          }}
          {...props}
        />
      </>
    )
  }

  return (
    <span className={clsx("relative overflow-hidden inline-block", className)}>
      {skeletonEl}
      <Image
        width={width}
        height={height}
        className={clsx(
          className,
          "transition-opacity duration-300",
          loaded ? "opacity-100" : "opacity-0"
        )}
        onLoad={(e) => {
          setLoaded(true)
          onLoad?.(e)
        }}
        {...props}
      />
    </span>
  )
}

interface AvatarProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  size: number
  src: string
}

export const Avatar: React.FC<AvatarProps> = ({ size = 30, src, ...props }) => {
  const fallback = `https://singlecolorimage.com/get/979797/${size}x${size}`
  return (
    <AppImage
      src={src.length !== 0 ? src : fallback}
      className={clsx(props.className, "rounded-full object-cover")}
      width={size}
      height={size}
      alt={props.alt ?? ""}
    />
  )
}

interface ImageCoverProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  size: [width: number, height: number]
  src: string
  rounded?: string
}

export const ImageCover: React.FC<ImageCoverProps> = ({
  size,
  src,
  rounded = "rounded",
  ...props
}) => {
  const fallback = `https://singlecolorimage.com/get/979797/${size[0]}x${size[1]}`
  return (
    <AppImage
      src={src.length !== 0 ? src : fallback}
      width={size[0]}
      height={size[1]}
      className={clsx(rounded, "object-cover", props.className)}
      alt={props.alt ?? ""}
    />
  )
}

// @ts-ignore
// export const BasicImage: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
//   return <img {...props} alt={props.alt} />
// }

interface BasicImageProps {
  src: string
  width: number
  height: number
  alt: string
  className?: string
}

export const BasicImage: React.FC<BasicImageProps> = ({ src, className, ...props }) => {
  return (
    <AppImage
      src={src ?? `/1000x1000.png`}
      className={clsx("object-cover", className)}
      // @ts-ignore
      alt={""}
      {...props}
    />
  )
}
